import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useServiceWaapDomainsPaged } from '@/api/manticore/service-waap';

/**
 * WAAP website (domain) context. The WAAP config endpoints are per-domain
 * (`/waap/domains/:domainId/...`), but the console is service-scoped — so the
 * console holds a "currently selected website" that the per-website tabs read.
 *
 * The website list is sourced from the WAAP service's domain registry
 * (`/services/:id/waap/domains`, type='waap' bindings) — a DB-backed list that
 * yields the domain UUIDs the WAAP config calls need. It is fully separate from
 * the CDN domain list. (The vendor `strategies` feed is account-wide and used to
 * enrich WAF info, not as the id source.)
 */
export interface WaapWebsite {
  /** Domain UUID — the `:id` for /waap/domains/:id/... config calls. */
  domainId: string;
  domain: string;
}

interface WaapWebsiteContextValue {
  websites: WaapWebsite[];
  selected: WaapWebsite | null;
  setSelectedDomainId: (domainId: string) => void;
  isLoading: boolean;
}

const WaapWebsiteContext = createContext<WaapWebsiteContextValue | null>(null);

export function WaapWebsiteProvider({
  serviceId,
  children,
}: {
  serviceId: string;
  children: ReactNode;
}) {
  const { data, isLoading } = useServiceWaapDomainsPaged(serviceId, { page: 1, perPage: 100 });

  const websites = useMemo<WaapWebsite[]>(
    () =>
      (data?.data ?? [])
        .filter((d) => !!d.id)
        .map((d) => ({ domainId: d.id as string, domain: d.domain })),
    [data],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Default to the first website once loaded; preserve the current pick if it
  // still exists in the refreshed list.
  useEffect(() => {
    if (!websites.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((cur) =>
      cur && websites.some((w) => w.domainId === cur) ? cur : websites[0].domainId,
    );
  }, [websites]);

  const value = useMemo<WaapWebsiteContextValue>(() => {
    const selected = websites.find((w) => w.domainId === selectedId) ?? null;
    return { websites, selected, setSelectedDomainId: setSelectedId, isLoading };
  }, [websites, selectedId, isLoading]);

  return <WaapWebsiteContext.Provider value={value}>{children}</WaapWebsiteContext.Provider>;
}

/** Read the selected WAAP website. Throws outside a WaapWebsiteProvider. */
export function useWaapWebsite(): WaapWebsiteContextValue {
  const ctx = useContext(WaapWebsiteContext);
  if (!ctx) throw new Error('useWaapWebsite must be used within a WaapWebsiteProvider');
  return ctx;
}
